import { nowUTC } from "@homebound/activesupport";
import { ContentHeader, HeaderAction, useModal } from "@homebound/beam";
import { useCallback } from "react";
import { cycleTrackingAction } from "src/components/CycleTrackingAction";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  FeatureFlagType,
  PlanPackageIncomingSlotStatus,
  PotentialOperation2,
  SavePlanPackageInput,
} from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { GenerateTakeoffModal } from "src/routes/libraries/plan-package-v2/components/takeoffs/GenerateTakeoffModal";
import {
  comingWithSidePanel,
  useImportItems,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/useImportItems";
import { useSavePlanPackageTakeoffsCycleMutation } from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import { noop, pluralize } from "src/utils";

export function TakeoffsTabHeader() {
  const planPackage = useTakeoffsStore((state) => state.planPackage);
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);
  const planPackagesAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);
  const { id, version, incomingSlots, takeoffsStartedAt, takeoffsCompletedAt, canEditLineItems } = planPackage;
  const { openModal } = useModal();
  const importItems = useImportItems();

  const [saveTakeoffsCycle] = useSavePlanPackageTakeoffsCycleMutation();
  const saveCycle = useCallback(
    (input: Pick<SavePlanPackageInput, "takeoffsStartedAt" | "takeoffsCompletedAt">) =>
      saveTakeoffsCycle({ variables: { input: { id, ...input } } }),
    [saveTakeoffsCycle, id],
  );

  const pendingSlotCount = incomingSlots.count((s) => s.status.code === PlanPackageIncomingSlotStatus.Pending);
  const reviewSlotsAction: HeaderAction = {
    label: `${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`,
    variant: "caution",
    icon: "todo",
    onClick: createPlanPackageIncomingSlotReviewUrl(id, version.id),
  };
  const generateTakeoffAction: HeaderAction = {
    label: "Generate Takeoff",
    variant: "secondary",
    disabled: disableBasedOnPotentialOperation(canEditLineItems),
    onClick: () => openModal({ size: "lg", aiMode: true, drawHeaderBorder: true, content: <GenerateTakeoffModal /> }),
  };
  const actions: HeaderAction[] = [
    ...(designerModifiedSlotsEnabled && pendingSlotCount > 0 ? [reviewSlotsAction] : []),
    // Not gated on the `PlanPackagesTakeoffAi` feature flag since Generate Takeoff supports both states:
    // - On: creates AEPs for review
    // - Off: directly creates/updates TLIs
    ...(planPackagesAiEnabled ? [generateTakeoffAction] : []),
    cycleTrackingAction({
      title: "Plan Cycle",
      startedAt: takeoffsStartedAt?.toPlainDate(),
      completedAt: takeoffsCompletedAt?.toPlainDate(),
      onStart: () => saveCycle({ takeoffsStartedAt: nowUTC() }),
      onMarkComplete: () => saveCycle({ takeoffsCompletedAt: nowUTC() }),
      onReopen: () => saveCycle({ takeoffsCompletedAt: null }),
      onReset: () => saveCycle({ takeoffsStartedAt: null, takeoffsCompletedAt: null }),
    }),
    createAddMenu(canEditLineItems, importItems),
  ];

  return <ContentHeader title="Takeoffs" actions={actions} />;
}

/** The Takeoffs header's Add menu. */
function createAddMenu(canEditLineItems: PotentialOperation2, importItems: VoidFunction): HeaderAction {
  return {
    kind: "menu",
    trigger: { label: "Add" },
    disabled: disableBasedOnPotentialOperation(canEditLineItems),
    items: [
      // TODO: PLAN-1939 adds lines in the side panel
      { label: "Add Labor", icon: "wrench", onClick: noop, disabled: comingWithSidePanel },
      { label: "Add Material", icon: "cube", onClick: noop, disabled: comingWithSidePanel },
      { label: "Add Placeholder", icon: "cubeDashed", onClick: noop, disabled: comingWithSidePanel },
      { label: "Import Items", icon: "upload", onClick: importItems },
    ],
  };
}
